// Événements du site (Panel admin > Site > Événements) : Noël, Halloween, Nouvel An…
// Le serveur garde seulement les réglages (mode, événement choisi, options) ; les dates
// sont calculées ici, dans le navigateur, à l'heure du visiteur ou à l'heure de France.

export const SEASON_CATEGORIES = [
  { id: "fetes", label: "Grandes fêtes" },
  { id: "france", label: "France" },
  { id: "cultures", label: "Cultures et religions" },
  { id: "commerce", label: "Shopping" },
  { id: "causes", label: "Associations et causes" },
  { id: "fun", label: "Pour le fun" },
];

// Effets animés par-dessus le site (voir SeasonalLayer).
export const SEASON_EFFECTS = {
  none: { label: "Aucun" },
  snow: { label: "Neige", kind: "snow" },
  confetti: { label: "Confettis", kind: "confetti" },
  fireworks: { label: "Feux d'artifice", kind: "fireworks" },
  tricolore: { label: "Confettis bleu blanc rouge", kind: "confetti", colors: ["#1d4ed8", "#ffffff", "#ef4444"] },
  hearts: { label: "Cœurs", kind: "emoji", set: ["❤️", "💕", "💖", "🌹"], rise: true },
  halloween: { label: "Citrouilles et chauves-souris", kind: "emoji", set: ["🎃", "🦇", "👻", "🕸️"] },
  leaves: { label: "Feuilles d'automne", kind: "emoji", set: ["🍂", "🍁", "🍂"] },
  eggs: { label: "Œufs et chocolats", kind: "emoji", set: ["🥚", "🐣", "🍫", "🌷"] },
  clovers: { label: "Trèfles", kind: "emoji", set: ["☘️", "🍀"] },
  flowers: { label: "Fleurs", kind: "emoji", set: ["🌸", "🌼", "🌷", "💐"] },
  crepes: { label: "Crêpes", kind: "emoji", set: ["🥞", "🧈", "🍯"] },
  galette: { label: "Couronnes", kind: "emoji", set: ["👑", "🥧", "✨"] },
  fish: { label: "Poissons", kind: "emoji", set: ["🐟", "🐠", "🐡"] },
  music: { label: "Notes de musique", kind: "emoji", set: ["🎵", "🎶", "🎸", "🥁"], rise: true },
  lanterns: { label: "Lanternes", kind: "emoji", set: ["🏮", "🧧", "🐉", "✨"], rise: true },
  stars: { label: "Étoiles et croissants", kind: "emoji", set: ["🌙", "⭐", "✨"] },
  candles: { label: "Bougies", kind: "emoji", set: ["🕎", "✨", "🕯️"], rise: true },
  diya: { label: "Lampes à huile", kind: "emoji", set: ["🪔", "✨", "🌼"], rise: true },
  shopping: { label: "Étiquettes promo", kind: "emoji", set: ["🛍️", "🏷️", "💸", "🎁"] },
  ribbons: { label: "Rubans", kind: "emoji", set: ["🎗️", "💗", "🎀"] },
  earth: { label: "Planète et feuilles", kind: "emoji", set: ["🌍", "🌱", "🍃"] },
  rainbow: { label: "Arc-en-ciel", kind: "confetti", colors: ["#e40303", "#ff8c00", "#ffed00", "#008026", "#24408e", "#732982"] },
  school: { label: "Fournitures", kind: "emoji", set: ["📚", "✏️", "🎒", "📐"] },
  space: { label: "Étoiles de l'espace", kind: "emoji", set: ["⭐", "✨", "🌌", "🪐"] },
  grapes: { label: "Raisin", kind: "emoji", set: ["🍇", "🍷"] },
  gifts: { label: "Cadeaux", kind: "emoji", set: ["🎁", "🎀", "✨"] },
};

// ---------- calendrier ----------

const pad = (n) => String(n).padStart(2, "0");
const ymd = (y, m, d) => {
  const t = new Date(Date.UTC(y, m - 1, d));
  return { y: t.getUTCFullYear(), m: t.getUTCMonth() + 1, d: t.getUTCDate() };
};
const addDays = (date, n) => ymd(date.y, date.m, date.d + n);
const weekday = (y, m, d) => new Date(Date.UTC(y, m - 1, d)).getUTCDay();
const fixed = (m, d) => (y) => ({ y, m, d });

// n-ième jour de la semaine du mois (wd : 0 = dimanche) ; n = -1 pour le dernier.
function nthWeekday(y, m, wd, n) {
  if (n > 0) {
    const first = weekday(y, m, 1);
    return ymd(y, m, 1 + ((wd - first + 7) % 7) + (n - 1) * 7);
  }
  const lastDay = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const last = weekday(y, m, lastDay);
  return ymd(y, m, lastDay - ((last - wd + 7) % 7));
}

// Dimanche de Pâques (calendrier grégorien).
function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4;
  const f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30, i = Math.floor(c / 4), k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7, m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31), day = ((h + l - 7 * m + 114) % 31) + 1;
  return { y, m: month, d: day };
}
const fromEaster = (n) => (y) => addDays(easter(y), n);

// Fête des mères en France : dernier dimanche de mai, ou premier dimanche de juin
// quand il tombe le jour de la Pentecôte.
function mothersDay(y) {
  const last = nthWeekday(y, 5, 0, -1);
  const pentecost = addDays(easter(y), 49);
  return last.m === pentecost.m && last.d === pentecost.d ? nthWeekday(y, 6, 0, 1) : last;
}

// Soldes : d'hiver le 2e mercredi de janvier (le 1er s'il tombe après le 12),
// d'été le dernier mercredi de juin (l'avant-dernier s'il tombe après le 28).
function winterSales(y) {
  const second = nthWeekday(y, 1, 3, 2);
  return second.d > 12 ? nthWeekday(y, 1, 3, 1) : second;
}
function summerSales(y) {
  const last = nthWeekday(y, 6, 3, -1);
  return last.d > 28 ? addDays(last, -7) : last;
}

// Fêtes à calendrier lunaire : dates connues (tableau), puis conversion du navigateur (Intl)
// pour les années au-delà du tableau.
const calCache = {};
function scanCalendar(y, calendar, opts, match) {
  const key = `${calendar}:${y}:${match.key}`;
  if (key in calCache) return calCache[key];
  let found = [];
  try {
    const fmt = new Intl.DateTimeFormat(`en-US-u-ca-${calendar}`, { timeZone: "UTC", ...opts });
    if (fmt.resolvedOptions().calendar === calendar) {
      for (let i = 0; i < 366; i++) {
        const t = new Date(Date.UTC(y, 0, 1 + i));
        if (t.getUTCFullYear() !== y) break;
        const parts = {};
        fmt.formatToParts(t).forEach((p) => { parts[p.type] = p.value; });
        if (match(parts)) found.push({ y, m: t.getUTCMonth() + 1, d: t.getUTCDate() });
      }
    } else found = null;
  } catch {
    found = null;
  }
  calCache[key] = found;
  return found;
}
const fromTable = (table) => (y) => (table[y] || []).map(([m, d]) => ({ y, m, d }));
function lunar(calendar, opts, match, table) {
  return (y) => (table[y] ? fromTable(table)(y) : scanCalendar(y, calendar, opts, match) || []);
}
const hijri = (month, day) => {
  const match = (p) => p.month === String(month) && p.day === String(day);
  match.key = `${month}-${day}`;
  return match;
};
const RAMADAN = { 2025: [[3, 1]], 2026: [[2, 18]], 2027: [[2, 8]], 2028: [[1, 28]], 2029: [[1, 16]], 2030: [[1, 6], [12, 26]] };
const EID_FITR = { 2025: [[3, 30]], 2026: [[3, 20]], 2027: [[3, 9]], 2028: [[2, 26]], 2029: [[2, 14]], 2030: [[2, 4]] };
const EID_ADHA = { 2025: [[6, 6]], 2026: [[5, 27]], 2027: [[5, 16]], 2028: [[5, 5]], 2029: [[4, 24]], 2030: [[4, 13]] };
const LUNAR_NEW_YEAR = { 2025: [[1, 29]], 2026: [[2, 17]], 2027: [[2, 6]], 2028: [[1, 26]], 2029: [[2, 13]], 2030: [[2, 3]] };
const HANUKKAH = { 2025: [[12, 14]], 2026: [[12, 4]], 2027: [[12, 24]], 2028: [[12, 12]], 2029: [[12, 1]], 2030: [[12, 20]] };
const DIWALI = { 2025: [[10, 20]], 2026: [[11, 8]], 2027: [[10, 29]], 2028: [[10, 17]], 2029: [[11, 5]], 2030: [[10, 26]] };
const firstChineseMonth = (p) => p.month === "1" && p.day === "1";
firstChineseMonth.key = "1-1";
const kislev25 = (p) => p.month === "Kislev" && p.day === "25";
kislev25.key = "kislev-25";
// Hanoucca commence le soir, la veille du 25 Kislev.
const hanukkah = (y) => {
  if (HANUKKAH[y]) return fromTable(HANUKKAH)(y);
  return (scanCalendar(y, "hebrew", { month: "long", day: "numeric" }, kislev25) || []).map((date) => addDays(date, -1));
};

// ---------- catalogue ----------
// when(année) → date (ou liste de dates) ; before : jours de compte à rebours avant ;
// span : jours de fête ; hour : heure du début ; priority : départage en mode automatique.
export const SEASON_EVENTS = [
  { id: "noel", cat: "fetes", name: "Noël", emoji: "🎄", when: fixed(12, 25), before: 24, effect: "snow", colors: ["#ef4444", "#22c55e"], label: "avant Noël", live: "Joyeux Noël ! 🎄", priority: 9 },
  { id: "nouvel-an", cat: "fetes", name: "Nouvel An", emoji: "🎆", when: fixed(1, 1), before: 6, effect: "fireworks", colors: ["#facc15", "#a855f7"], label: "avant la nouvelle année", live: "Bonne année {year} ! 🎉", priority: 10 },
  { id: "fetes-fin-annee", cat: "fetes", name: "Fêtes de fin d'année", emoji: "✨", when: fixed(1, 1), before: 31, effect: "snow", colors: ["#facc15", "#ef4444"], label: "avant le passage à {year}", live: "Bonne année {year} ! 🥂", priority: 2, auto: false },
  { id: "halloween", cat: "fetes", name: "Halloween", emoji: "🎃", when: fixed(10, 31), before: 14, effect: "halloween", colors: ["#f97316", "#7c3aed"], label: "avant Halloween", live: "Joyeux Halloween ! 🎃 Des bonbons ou un sort ?", priority: 8 },
  { id: "saint-valentin", cat: "fetes", name: "Saint-Valentin", emoji: "💘", when: fixed(2, 14), before: 7, effect: "hearts", colors: ["#ec4899", "#ef4444"], label: "avant la Saint-Valentin", live: "Joyeuse Saint-Valentin ! 💘", priority: 8 },
  { id: "paques", cat: "fetes", name: "Pâques", emoji: "🐣", when: fromEaster(0), before: 7, span: 2, effect: "eggs", colors: ["#facc15", "#a3e635"], label: "avant Pâques", live: "Joyeuses Pâques ! 🐣", priority: 7 },
  { id: "fete-meres", cat: "fetes", name: "Fête des mères", emoji: "💐", when: mothersDay, before: 5, effect: "flowers", colors: ["#f472b6", "#fb7185"], label: "avant la fête des mères", live: "Bonne fête à toutes les mamans ! 💐", priority: 6 },
  { id: "fete-peres", cat: "fetes", name: "Fête des pères", emoji: "👔", when: (y) => nthWeekday(y, 6, 0, 3), before: 5, effect: "confetti", colors: ["#3b82f6", "#0ea5e9"], label: "avant la fête des pères", live: "Bonne fête à tous les papas ! 👔", priority: 6 },
  { id: "fete-grands-meres", cat: "fetes", name: "Fête des grands-mères", emoji: "👵", when: (y) => nthWeekday(y, 3, 0, 1), before: 3, effect: "flowers", colors: ["#c084fc", "#f9a8d4"], label: "avant la fête des grands-mères", live: "Bonne fête aux mamies ! 👵", priority: 4, auto: false },
  { id: "saint-nicolas", cat: "fetes", name: "Saint-Nicolas", emoji: "🎅", when: fixed(12, 6), before: 3, effect: "gifts", colors: ["#ef4444", "#facc15"], label: "avant la Saint-Nicolas", live: "Bonne Saint-Nicolas ! 🎅", priority: 3, auto: false },

  { id: "epiphanie", cat: "france", name: "Épiphanie (galette des rois)", emoji: "👑", when: fixed(1, 6), before: 3, effect: "galette", colors: ["#facc15", "#f59e0b"], label: "avant l'Épiphanie", live: "Qui aura la fève ? 👑", priority: 5 },
  { id: "chandeleur", cat: "france", name: "Chandeleur", emoji: "🥞", when: fixed(2, 2), before: 3, effect: "crepes", colors: ["#f59e0b", "#fde68a"], label: "avant la Chandeleur", live: "C'est la Chandeleur : à vos crêpes ! 🥞", priority: 5 },
  { id: "mardi-gras", cat: "france", name: "Mardi gras / Carnaval", emoji: "🎭", when: fromEaster(-47), before: 5, effect: "confetti", colors: ["#a855f7", "#22c55e"], label: "avant Mardi gras", live: "Joyeux Mardi gras ! 🎭", priority: 5 },
  { id: "poisson-avril", cat: "france", name: "Poisson d'avril", emoji: "🐟", when: fixed(4, 1), before: 2, effect: "fish", colors: ["#0ea5e9", "#22d3ee"], label: "avant le 1er avril", live: "Poisson d'avril ! 🐟 Attention aux blagues…", priority: 6 },
  { id: "fete-travail", cat: "france", name: "Fête du Travail (1er mai)", emoji: "🌿", when: fixed(5, 1), before: 3, effect: "flowers", colors: ["#22c55e", "#f8fafc"], label: "avant le 1er mai", live: "Bon 1er mai ! Un brin de muguet 🌿", priority: 5 },
  { id: "victoire-1945", cat: "france", name: "Victoire du 8 mai 1945", emoji: "🕊️", when: fixed(5, 8), before: 1, effect: "tricolore", colors: ["#2563eb", "#ef4444"], label: "avant le 8 mai", live: "8 mai 1945 : nous n'oublions pas. 🕊️", priority: 3, auto: false },
  { id: "fete-musique", cat: "france", name: "Fête de la musique", emoji: "🎶", when: fixed(6, 21), before: 5, effect: "music", colors: ["#a855f7", "#06b6d4"], label: "avant la Fête de la musique", live: "Bonne Fête de la musique ! 🎶 Monte le son.", priority: 6 },
  { id: "14-juillet", cat: "france", name: "Fête nationale (14 juillet)", emoji: "🇫🇷", when: fixed(7, 14), before: 5, effect: "fireworks", colors: ["#2563eb", "#ef4444"], label: "avant le 14 juillet", live: "Bonne fête nationale ! 🇫🇷🎆", priority: 7 },
  { id: "rentree", cat: "france", name: "Rentrée scolaire", emoji: "🎒", when: fixed(9, 1), before: 7, effect: "school", colors: ["#3b82f6", "#facc15"], label: "avant la rentrée", live: "Bonne rentrée à tous ! 🎒", priority: 4 },
  { id: "patrimoine", cat: "france", name: "Journées du patrimoine", emoji: "🏛️", when: (y) => addDays(nthWeekday(y, 9, 0, 3), -1), span: 2, before: 3, effect: "leaves", colors: ["#b45309", "#facc15"], label: "avant les Journées du patrimoine", live: "Journées du patrimoine : les monuments ouvrent leurs portes. 🏛️", priority: 2, auto: false },
  { id: "armistice", cat: "france", name: "Armistice (11 novembre)", emoji: "🕊️", when: fixed(11, 11), before: 1, effect: "tricolore", colors: ["#2563eb", "#ef4444"], label: "avant le 11 novembre", live: "11 novembre 1918 : nous n'oublions pas. 🕊️", priority: 3, auto: false },
  { id: "beaujolais", cat: "france", name: "Beaujolais nouveau", emoji: "🍇", when: (y) => nthWeekday(y, 11, 4, 3), before: 2, effect: "grapes", colors: ["#7e22ce", "#be123c"], label: "avant le Beaujolais nouveau", live: "Le Beaujolais nouveau est arrivé ! 🍇", priority: 2, auto: false },
  { id: "automne", cat: "france", name: "Automne", emoji: "🍁", when: fixed(9, 22), before: 0, span: 40, effect: "leaves", colors: ["#ea580c", "#facc15"], label: "avant l'automne", live: "C'est l'automne 🍁", priority: 1, auto: false },
  { id: "hiver", cat: "france", name: "Hiver", emoji: "❄️", when: fixed(12, 21), before: 0, span: 60, effect: "snow", colors: ["#38bdf8", "#e0f2fe"], label: "avant l'hiver", live: "L'hiver est là ❄️", priority: 1, auto: false },

  { id: "nouvel-an-lunaire", cat: "cultures", name: "Nouvel An lunaire", emoji: "🧧", when: lunar("chinese", { month: "numeric", day: "numeric" }, firstChineseMonth, LUNAR_NEW_YEAR), before: 5, span: 3, effect: "lanterns", colors: ["#dc2626", "#facc15"], label: "avant le Nouvel An lunaire", live: "Bonne année lunaire ! 🧧", priority: 6 },
  { id: "ramadan", cat: "cultures", name: "Ramadan", emoji: "🌙", when: lunar("islamic-umalqura", { month: "numeric", day: "numeric" }, hijri(9, 1), RAMADAN), before: 3, span: 29, effect: "stars", colors: ["#0d9488", "#facc15"], label: "avant le Ramadan", live: "Ramadan Moubarak ! 🌙", priority: 3, showEnd: true },
  { id: "aid-el-fitr", cat: "cultures", name: "Aïd el-Fitr", emoji: "🌙", when: lunar("islamic-umalqura", { month: "numeric", day: "numeric" }, hijri(10, 1), EID_FITR), before: 3, span: 2, effect: "stars", colors: ["#16a34a", "#facc15"], label: "avant l'Aïd el-Fitr", live: "Aïd Moubarak ! 🌙", priority: 6 },
  { id: "aid-el-kebir", cat: "cultures", name: "Aïd el-Kébir", emoji: "🌙", when: lunar("islamic-umalqura", { month: "numeric", day: "numeric" }, hijri(12, 10), EID_ADHA), before: 3, span: 2, effect: "stars", colors: ["#16a34a", "#facc15"], label: "avant l'Aïd el-Kébir", live: "Aïd Moubarak ! 🌙", priority: 6 },
  { id: "hanoucca", cat: "cultures", name: "Hanoucca", emoji: "🕎", when: hanukkah, hour: 18, before: 3, span: 8, effect: "candles", colors: ["#2563eb", "#facc15"], label: "avant Hanoucca", live: "Hanoucca Sameah ! 🕎", priority: 4, showEnd: true },
  { id: "diwali", cat: "cultures", name: "Diwali", emoji: "🪔", when: fromTable(DIWALI), before: 3, span: 2, effect: "diya", colors: ["#f59e0b", "#db2777"], label: "avant Diwali", live: "Joyeux Diwali ! 🪔", priority: 4 },
  { id: "saint-patrick", cat: "cultures", name: "Saint-Patrick", emoji: "☘️", when: fixed(3, 17), before: 3, effect: "clovers", colors: ["#16a34a", "#84cc16"], label: "avant la Saint-Patrick", live: "Joyeuse Saint-Patrick ! ☘️", priority: 5 },

  { id: "soldes-hiver", cat: "commerce", name: "Soldes d'hiver", emoji: "🏷️", when: winterSales, hour: 8, before: 7, span: 28, effect: "shopping", colors: ["#ef4444", "#facc15"], label: "avant les soldes d'hiver", live: "C'est les soldes ! 🏷️", priority: 2, showEnd: true, auto: false },
  { id: "soldes-ete", cat: "commerce", name: "Soldes d'été", emoji: "🏷️", when: summerSales, hour: 8, before: 7, span: 28, effect: "shopping", colors: ["#f97316", "#facc15"], label: "avant les soldes d'été", live: "C'est les soldes ! 🏷️", priority: 2, showEnd: true, auto: false },
  { id: "black-friday", cat: "commerce", name: "Black Friday", emoji: "🛍️", when: (y) => addDays(nthWeekday(y, 11, 4, 4), 1), before: 7, span: 1, effect: "shopping", colors: ["#111827", "#facc15"], label: "avant le Black Friday", live: "C'est le Black Friday ! 🛍️", priority: 7, showEnd: true },
  { id: "cyber-monday", cat: "commerce", name: "Cyber Monday", emoji: "💻", when: (y) => addDays(nthWeekday(y, 11, 4, 4), 4), before: 2, effect: "shopping", colors: ["#06b6d4", "#6366f1"], label: "avant le Cyber Monday", live: "C'est le Cyber Monday ! 💻", priority: 6, showEnd: true },

  { id: "octobre-rose", cat: "causes", name: "Octobre Rose (cancer du sein)", emoji: "🎗️", when: fixed(10, 1), before: 0, span: 31, effect: "ribbons", colors: ["#ec4899", "#f9a8d4"], label: "avant Octobre Rose", live: "Octobre Rose : ensemble contre le cancer du sein. 🎗️", priority: 3 },
  { id: "movember", cat: "causes", name: "Movember (santé masculine)", emoji: "🥸", when: fixed(11, 1), before: 0, span: 30, effect: "confetti", colors: ["#0ea5e9", "#1e3a8a"], label: "avant Movember", live: "Movember : la moustache pour la santé des hommes. 🥸", priority: 2 },
  { id: "mois-sans-tabac", cat: "causes", name: "Mois sans tabac", emoji: "🚭", when: fixed(11, 1), before: 0, span: 30, effect: "earth", colors: ["#22c55e", "#0ea5e9"], label: "avant le Mois sans tabac", live: "Mois sans tabac : on arrête ensemble ! 🚭", priority: 1, auto: false },
  { id: "telethon", cat: "causes", name: "Téléthon", emoji: "💛", when: (y) => nthWeekday(y, 12, 5, 1), hour: 18, before: 5, span: 2, effect: "confetti", colors: ["#facc15", "#ef4444"], label: "avant le Téléthon", live: "C'est le Téléthon : tous ensemble ! 💛", priority: 5 },
  { id: "sida", cat: "causes", name: "Journée mondiale contre le sida", emoji: "🎗️", when: fixed(12, 1), before: 1, effect: "ribbons", colors: ["#dc2626", "#f87171"], label: "avant le 1er décembre", live: "1er décembre : ensemble contre le sida. 🎗️", priority: 4 },
  { id: "droits-femmes", cat: "causes", name: "Journée des droits des femmes", emoji: "💜", when: fixed(3, 8), before: 2, effect: "flowers", colors: ["#a855f7", "#ec4899"], label: "avant le 8 mars", live: "8 mars : journée internationale des droits des femmes. 💜", priority: 4 },
  { id: "autisme", cat: "causes", name: "Journée de l'autisme", emoji: "💙", when: fixed(4, 2), before: 1, effect: "confetti", colors: ["#3b82f6", "#60a5fa"], label: "avant le 2 avril", live: "2 avril : journée mondiale de sensibilisation à l'autisme. 💙", priority: 3, auto: false },
  { id: "jour-terre", cat: "causes", name: "Jour de la Terre", emoji: "🌍", when: fixed(4, 22), before: 2, effect: "earth", colors: ["#22c55e", "#0ea5e9"], label: "avant le Jour de la Terre", live: "Jour de la Terre : prenons soin de notre planète. 🌍", priority: 3 },
  { id: "environnement", cat: "causes", name: "Journée de l'environnement", emoji: "🌱", when: fixed(6, 5), before: 1, effect: "earth", colors: ["#16a34a", "#84cc16"], label: "avant le 5 juin", live: "5 juin : journée mondiale de l'environnement. 🌱", priority: 2, auto: false },
  { id: "mois-fiertes", cat: "causes", name: "Mois des fiertés", emoji: "🏳️‍🌈", when: fixed(6, 1), before: 0, span: 30, effect: "rainbow", colors: ["#e40303", "#732982"], label: "avant le Mois des fiertés", live: "Mois des fiertés : chacun a sa place ici. 🏳️‍🌈", priority: 2, auto: false },
  { id: "sante-mentale", cat: "causes", name: "Journée de la santé mentale", emoji: "🧠", when: fixed(10, 10), before: 1, effect: "confetti", colors: ["#22c55e", "#14b8a6"], label: "avant le 10 octobre", live: "10 octobre : prends soin de toi. 🧠", priority: 4, auto: false },
  { id: "droits-enfant", cat: "causes", name: "Journée des droits de l'enfant", emoji: "🧸", when: fixed(11, 20), before: 1, effect: "confetti", colors: ["#3b82f6", "#facc15"], label: "avant le 20 novembre", live: "20 novembre : journée internationale des droits de l'enfant. 🧸", priority: 3, auto: false },
  { id: "contre-cancer", cat: "causes", name: "Journée mondiale contre le cancer", emoji: "🎗️", when: fixed(2, 4), before: 1, effect: "ribbons", colors: ["#a855f7", "#f472b6"], label: "avant le 4 février", live: "4 février : journée mondiale contre le cancer. 🎗️", priority: 3, auto: false },

  { id: "star-wars", cat: "fun", name: "Star Wars Day (May the 4th)", emoji: "🌌", when: fixed(5, 4), before: 2, effect: "space", colors: ["#facc15", "#38bdf8"], label: "avant le 4 mai", live: "May the 4th be with you ! 🌌", priority: 4 },
  { id: "jeux-video", cat: "fun", name: "Journée du jeu vidéo", emoji: "🎮", when: fixed(7, 8), before: 2, effect: "confetti", colors: ["#6366f1", "#22d3ee"], label: "avant la Journée du jeu vidéo", live: "Bonne Journée du jeu vidéo ! 🎮", priority: 3, auto: false },
  { id: "geek-pride", cat: "fun", name: "Geek Pride Day", emoji: "🤓", when: fixed(5, 25), before: 2, effect: "space", colors: ["#22c55e", "#6366f1"], label: "avant la Geek Pride", live: "Joyeuse Geek Pride ! 🤓", priority: 2, auto: false },
];

export const CUSTOM_EVENT_ID = "custom";

// Réglages par défaut (identiques à ceux du serveur).
export const DEFAULT_SEASON = {
  mode: "off", event: "noel", countdown: true, banner: true, effects: true, theme: true, eggs: true,
  timezone: "visitor", message: "", auto_events: null, custom: null,
};

export const defaultAutoEvents = () => SEASON_EVENTS.filter((e) => e.auto !== false).map((e) => e.id);

// Événement personnalisé (créé dans le panel) → même forme que ceux du catalogue.
export function customEvent(custom) {
  if (!custom || !custom.date) return null;
  const match = /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?$/.exec(custom.date);
  if (!match) return null;
  const [, Y, M, D, h, mi] = match;
  const yearly = Boolean(custom.yearly);
  const name = custom.name || "Événement";
  return {
    id: CUSTOM_EVENT_ID, cat: "custom", name, emoji: custom.emoji || "🎉",
    when: (y) => (yearly || y === +Y ? { y, m: +M, d: +D } : []),
    hour: +(h || 0), minute: +(mi || 0),
    before: custom.before === "" || custom.before == null || Number.isNaN(+custom.before) ? 7 : Math.max(0, Math.min(366, +custom.before)),
    span: Math.max(1, Math.min(366, +custom.span || 1)),
    effect: SEASON_EFFECTS[custom.effect] ? custom.effect : "confetti",
    colors: [custom.color || "#6366f1", custom.color2 || custom.color || "#a855f7"],
    label: custom.label || `avant ${name}`, live: custom.live || `${name} ${custom.emoji || "🎉"}`,
    priority: 20,
  };
}

export function findSeasonEvent(id, custom) {
  if (id === CUSTOM_EVENT_ID) return customEvent(custom);
  return SEASON_EVENTS.find((e) => e.id === id) || null;
}

// ---------- fuseau horaire ----------

const tzFormats = {};
function tzOffset(ts, tz) {
  if (!tzFormats[tz]) {
    tzFormats[tz] = new Intl.DateTimeFormat("en-US", { timeZone: tz, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" });
  }
  const p = {};
  tzFormats[tz].formatToParts(new Date(ts)).forEach((x) => { p[x.type] = x.value; });
  return Date.UTC(+p.year, +p.month - 1, +p.day, +p.hour % 24, +p.minute, +p.second) - Math.floor(ts / 1000) * 1000;
}

export const SEASON_TIMEZONES = {
  visitor: "Heure du visiteur (son pays)",
  "Europe/Paris": "Heure de France (Paris)",
};

// Instant (ms) où il est y-m-d h:mi dans le fuseau choisi ("visitor" = celui du navigateur).
export function zonedTime(date, hour, minute, tz) {
  if (!tz || tz === "visitor") return new Date(date.y, date.m - 1, date.d, hour || 0, minute || 0).getTime();
  try {
    const guess = Date.UTC(date.y, date.m - 1, date.d, hour || 0, minute || 0);
    const first = guess - tzOffset(guess, tz);
    return guess - tzOffset(first, tz);
  } catch {
    return new Date(date.y, date.m - 1, date.d, hour || 0, minute || 0).getTime();
  }
}

// ---------- occurrences ----------

const DAY = 86400000;

function occurrences(ev, around, tz) {
  const year = new Date(around).getFullYear();
  const out = [];
  for (let y = year - 1; y <= year + 1; y++) {
    let dates = ev.when(y);
    if (!dates) continue;
    if (!Array.isArray(dates)) dates = [dates];
    dates.forEach((date) => {
      const start = zonedTime(date, ev.hour, ev.minute, tz);
      // fin : minuit après le dernier jour (ou même heure pour une fête qui commence le soir)
      const end = zonedTime(addDays(date, ev.span || 1), ev.hour >= 12 ? ev.hour : 0, 0, tz);
      const windowStart = zonedTime(addDays(date, -(ev.before || 0)), 0, 0, tz);
      out.push({ date, start, end: Math.max(end, start + 60000), windowStart });
    });
  }
  return out.sort((a, b) => a.start - b.start);
}

// Prochaine (ou actuelle) occurrence d'un événement.
export function nextOccurrence(ev, now, tz) {
  const list = occurrences(ev, now, tz).concat(occurrences(ev, now + 366 * DAY, tz));
  return list.find((o) => o.end > now) || null;
}

function describe(ev, occ, now, cfg) {
  const phase = now < occ.start ? "countdown" : "live";
  const year = occ.date.y;
  const fill = (text) => (text || "").replace(/\{year\}/g, String(year));
  return {
    event: ev, phase, start: occ.start, end: occ.end,
    target: phase === "countdown" ? occ.start : (ev.showEnd || (ev.span || 1) > 1 ? occ.end : null),
    title: ev.name, emoji: ev.emoji,
    label: fill(ev.label),
    message: fill(cfg.message) || (phase === "live" ? fill(ev.live) : ""),
    live: fill(ev.live),
    effect: ev.effect, colors: ev.colors,
    key: `${ev.id}:${occ.start}`,
  };
}

// L'événement à afficher maintenant d'après les réglages du panel, ou null.
export function resolveSeason(cfg, now = Date.now()) {
  if (!cfg || !cfg.mode || cfg.mode === "off") return null;
  const tz = cfg.timezone || "visitor";
  if (cfg.mode === "manual") {
    const ev = findSeasonEvent(cfg.event, cfg.custom);
    if (!ev) return null;
    const occ = nextOccurrence(ev, now, tz);
    return occ ? describe(ev, occ, now, cfg) : null;
  }
  const enabled = new Set(Array.isArray(cfg.auto_events) ? cfg.auto_events : defaultAutoEvents());
  const candidates = SEASON_EVENTS.filter((e) => enabled.has(e.id));
  const custom = customEvent(cfg.custom);
  if (custom && enabled.has(CUSTOM_EVENT_ID)) candidates.push(custom);
  let best = null;
  candidates.forEach((ev) => {
    occurrences(ev, now, tz).forEach((occ) => {
      if (now < occ.windowStart || now >= occ.end) return;
      // la priorité d'abord (Halloween passe devant Octobre Rose), une fête en cours
      // gagne 2 points, puis la plus proche
      const score = (ev.priority || 0) + (now >= occ.start ? 2 : 0);
      const near = -Math.abs(occ.start - now);
      if (!best || score > best.score || (score === best.score && near > best.near)) best = { ev, occ, score, near };
    });
  });
  return best ? describe(best.ev, best.occ, now, cfg) : null;
}

// Prochains événements (calendrier du panel).
export function upcomingSeasons(cfg, now = Date.now(), limit = 10) {
  const tz = (cfg && cfg.timezone) || "visitor";
  const list = SEASON_EVENTS.map((ev) => ({ ev, occ: nextOccurrence(ev, now, tz) })).filter((x) => x.occ);
  const custom = customEvent(cfg && cfg.custom);
  if (custom) {
    const occ = nextOccurrence(custom, now, tz);
    if (occ) list.push({ ev: custom, occ });
  }
  return list.sort((a, b) => a.occ.start - b.occ.start).slice(0, limit);
}

export function splitCountdown(ms) {
  const total = Math.max(0, Math.floor(ms / 1000));
  return { days: Math.floor(total / 86400), hours: Math.floor((total % 86400) / 3600), minutes: Math.floor((total % 3600) / 60), seconds: total % 60 };
}

export const formatSeasonDate = (ts, tz) => {
  const opts = { weekday: "long", day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" };
  if (tz && tz !== "visitor") opts.timeZone = tz;
  try { return new Intl.DateTimeFormat("fr-FR", opts).format(new Date(ts)); } catch { return new Date(ts).toLocaleString("fr-FR"); }
};

export const isoDay = (date) => `${date.y}-${pad(date.m)}-${pad(date.d)}`;
